import { cva } from 'class-variance-authority';
import {
  ComponentType,
  ElementType,
  ReactNode,
  forwardRef,
  ForwardedRef,
  type JSX,
} from 'react';
import { AriaButtonOptions, useButton } from '@react-aria/button';
import styles from './Button.module.css';
import { useObjectRef, mergeProps } from '@react-aria/utils';
import { useHover, HoverProps } from '@react-aria/interactions';
import { useFocusRing } from '@react-aria/focus';
import Icon, { IconProps } from '../Icon/Icon';
import LoadingSpinner from '../LoadingSpinner';
import { focusStyleVariants } from '../../utils/focus/focusStyles';
import {
  generateResponsiveClasses,
  getBaseValue,
  WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const getResponsiveClasses = generateResponsiveClasses(styles);

const button = cva(styles.button, {
  variants: {
    size: {
      sm: styles['size--sm'],
      md: styles['size--md'],
      lg: styles['size--lg'],
    },
    accent: {
      primary: styles.accentPrimary,
      secondary: styles.accentSecondary,
      tertiary: styles.accentTertiary,
      quaternary: styles.accentQuaternary,
      secondaryFilled: styles.accentSecondaryFilled,
    },
    critical: {
      primary: styles.criticalPrimary,
      secondary: styles.criticalSecondary,
      tertiary: styles.criticalTertiary,
      quaternary: styles.criticalQuaternary,
      secondaryFilled: styles.criticalSecondaryFilled,
    },
    neutral: {
      primary: styles.neutralPrimary,
      secondary: styles.neutralSecondary,
      tertiary: styles.neutralTertiary,
      quaternary: styles.neutralQuaternary,
      secondaryFilled: styles.neutralSecondaryFilled,
    },
    white: {
      primary: styles.whitePrimary,
      secondary: styles.whiteSecondary,
      tertiary: styles.whiteTertiary,
      quaternary: styles.whiteQuaternary,
      secondaryFilled: styles.whiteSecondaryFilled,
    },
    fullWidth: {
      true: styles['full-width--true'],
      false: styles['full-width--false'],
    },
    focusStyle: {
      default: undefined,
      white: styles.focusWhite,
    },
    isLoading: {
      false: styles.isNotLoading,
      true: styles.isLoading,
    },
    textAlign: {
      left: styles['text-align--left'],
      center: styles['text-align--center'],
      right: styles['text-align--right'],
    },
    iconButton: {
      true: styles.iconButton,
    },
  },
  compoundVariants: [
    { size: 'md', accent: 'primary', className: styles.accentPrimary },
    { size: 'sm', iconButton: true, className: styles.iconButton__sm },
    { size: 'md', iconButton: true, className: styles.iconButton__md },
    { size: 'lg', iconButton: true, className: styles.iconButton__lg },
  ],
  defaultVariants: {
    size: 'md',
  },
});

type ButtonSize = 'lg' | 'md' | 'sm';
type ButtonTextAlignement = 'left' | 'center' | 'right';

export type ButtonBaseProps<T extends ElementType = 'button'> =
  AriaButtonOptions<T> &
    HoverProps & {
      /**
       * The HTML element to be rendered as the button.
       * @default 'button'
       * @example 'a' | 'button' | 'div'
       */
      as?: T;
      /**
       * For NextJS support. If `as` is used for next/link, this prop should be set to 'a' or the desired element for prop typing.
       * @default 'button'
       * @example 'a' | 'button'
       */
      elementType?: 'a' | 'button';
      /**
       * What the base style of button is
       * @default accent
       */
      styleVariant?: 'accent' | 'critical' | 'neutral' | 'white';

      /**
       * What the purpose is for the button
       * @default 'primary'
       */
      emphasis?:
        | 'primary'
        | 'secondary'
        | 'secondaryFilled'
        | 'tertiary'
        | 'quaternary';

      /**
       * **[Responsive]** The size of the button
       * @default 'md'
       */
      size?: ButtonSize;

      /**
       * Whether an icon should display at the end of Button and icon properties
       * @default
       */
      iconEndProps?: IconProps;

      /**
       * Whether an icon should display at the start of the Button and icon properties
       */
      iconStartProps?: IconProps;
      /**
       * isLoading is a boolean that will show a spinner in the button
       */
      isLoading?: boolean;

      /**
       * **[Responsive]** The text alignment of the button
       */
      textAlign?: ButtonTextAlignement;

      /**
       * The content for the button.
       */
      children?: ReactNode;

      /**
       * 	Whether the element should receive focus on render.
       */
      type?: 'button' | 'submit' | 'reset';

      /**
       * **[Responsive]** Whether the button should be full width.
       */
      fullWidth?: boolean;

      /**
       * The target of the link (if `as` is set to 'a').
       */
      target?: '_blank' | '_self' | '_parent' | '_top';

      /**
       *  What type of focus style to apply to the button.
       */
      focusStyle?: 'default' | 'white';

      /**
       * The slot of the button
       */
      slot?: string;

      /**
       * Internal prop used by `IconButton` to apply icon-button sizing and padding styles.
       * Do not use directly — use the `IconButton` component instead.
       * @internal
       * @ignore
       */
      iconButton?: boolean;

      /**
       * Passthrough props for the button element.
       */
      passthroughProps?: Partial<
        T extends ComponentType<infer P>
          ? P
          : T extends keyof JSX.IntrinsicElements
            ? JSX.IntrinsicElements[T]
            : never
      >;
    };

// ResponsivePropKeys are the keys of the props that can accept responsive values (base + breakpoint-specific)
type ResponsivePropKeys = 'size' | 'textAlign' | 'fullWidth';

export type ButtonProps<T extends ElementType = 'button'> = WithResponsiveProps<
  ButtonBaseProps<T>,
  ResponsivePropKeys
>;

export type DefaultButtonProps = ButtonProps<'button'>;

export const Button = forwardRef(function Button<
  T extends ElementType = 'button',
>(
  {
    as,
    elementType,
    styleVariant,
    emphasis = 'primary',
    size = 'md',
    iconStartProps,
    iconEndProps,
    children,
    fullWidth,
    textAlign = 'center',
    focusStyle = 'default',
    passthroughProps,
    isLoading = false,
    iconButton,
    slot,
    ...props
  }: ButtonProps<T>,
  // Not using <T> here as typescript cannot narrow it properly from consuming component
  // see - https://www.tsteele.dev/posts/react-polymorphic-forwardref for potential
  // solutions if proper typing is required.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forwardedRef?: ForwardedRef<any>,
) {
  const ref = useObjectRef(forwardedRef);

  // Call useButton to get the props and state needed for accessibility and interaction
  const { buttonProps, isPressed } = useButton(
    { ...props, elementType: elementType ?? as },
    ref,
  );
  const { hoverProps, isHovered } = useHover(props);
  const { isFocusVisible, focusProps, isFocused } = useFocusRing(props);

  const getAriaLabel = () => {
    if (isLoading) {
      return 'Loading Spinner';
    }

    const label =
      buttonProps['aria-label'] ||
      (typeof children === 'string' ? children : undefined);

    if (props.isDisabled && label) {
      return `${label}, unavailable`;
    }

    return label;
  };

  const baseSize = getBaseValue(size, 'md');
  const baseTextAlign = getBaseValue(textAlign, 'center');
  const baseFullWidth = getBaseValue(fullWidth, false);

  const sizeResponsiveClasses = getResponsiveClasses(size, 'size');
  const textAlignResponsiveClasses = getResponsiveClasses(
    textAlign,
    'text-align',
  );
  const fullWidthResponsiveClasses = getResponsiveClasses(
    fullWidth,
    'full-width',
  );

  const resolvedStyleVariant =
    styleVariant === 'white' ? 'neutral' : (styleVariant ?? 'accent');

  const className = button({
    size: baseSize,
    focusStyle,
    isLoading,
    textAlign: baseTextAlign,
    fullWidth: baseFullWidth,
    iconButton,
    [resolvedStyleVariant]: emphasis,
  });

  const Component: ElementType = as ?? 'button';

  return (
    <Component
      className={[
        className,
        focusStyleVariants({ focusStyle }),
        ...sizeResponsiveClasses,
        ...textAlignResponsiveClasses,
        ...fullWidthResponsiveClasses,
      ]
        .filter(Boolean)
        .join(' ')}
      ref={ref}
      data-focused={isFocused ? true : undefined}
      data-focus-visible={isFocusVisible ? true : undefined}
      data-hovered={isHovered ? true : undefined}
      data-pressed={isPressed ? true : undefined}
      aria-label={getAriaLabel()}
      aria-disabled={props.isDisabled ? true : undefined}
      data-testid="alto-button"
      slot={slot}
      {...mergeProps(buttonProps, hoverProps, focusProps)}
      {...passthroughProps}
    >
      <>
        {isLoading && (
          <LoadingSpinner
            className={styles.loadingSpinner}
            size={size === 'lg' ? '1.25x' : '1x'}
            aria-hidden="true"
          />
        )}
        {iconStartProps && <Icon {...iconStartProps} aria-hidden="true" />}
        <span className={styles.content}>{children}</span>
        {iconEndProps && <Icon {...iconEndProps} aria-hidden="true" />}
      </>
    </Component>
  );
});

export default Button;
